/* eslint-disable max-len */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation, useHistory, useParams } from 'react-router';
import { Alert, Button, Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import ReactJoyride, { STATUS } from 'react-joyride';
import classNames from 'classnames';
import { ForumWrapper } from 'components/ForumWrapper';
import ReactMarkdown from 'lib/react-markdown';
import ActionVideo from './components/ActionVideo';
import Checklist from 'routes/Products/components/Checklist';
import RecipesList from './components/RecipesList';
import ResourcesList from 'routes/Products/components/ResourcesListTwo';
import WeekNav from './components/WeekNav';
import { actions as userActions } from 'modules/user';
import * as styles from 'styles/index.scss';
import { getWeekNameDate } from 'lib/time';
import { useBootstrapBreakpoint } from 'components/VirtualMentorChat/utils/mobileUtils';
import { useWeekData, useOnboardingState, useScrollToComments } from './hooks';
import WeekContext from 'routes/Products/products/WLC/contexts/WeekContext';
import TrackWeekHeader from './components/TrackWeekHeader';
import TrackWeekInterstitial from './components/TrackWeekInterstitial';
import { OnboardSuccessModal, OnboardSkipModal } from './components/Modals';
import { OnboardingAlert } from './components/OnboardingComponents';
import {
  TOUR_STEPS,
  GETTING_STARTED_SLUG,
  GETTING_STARTED_RESOURCES,
  GETTING_STARTED_TUTORIAL_VIDEO_URL
} from './constants';
import { getProductFromPath, isGettingStartedSlug, createAnalyticsContext } from './utils';
import ActionHourSection, { ActionHourCard } from './components/ActionHourSection';
import NextLiveEventSection from './components/NextLiveEventSection';
import ResourcesSection from './components/ResourcesSection';
import ChecklistCard from './components/ChecklistCard';
import GettingStartedSuccessSection, { GettingStartedSuccessCard } from './components/GettingStartedSuccessSection';
import GettingStartedTrackList from './components/GettingStartedTrackList';
import GettingStartedTutorialSection from './components/GettingStartedTutorialSection';
import WeekSkeleton from './Week.skeleton';
import { useWeek } from '../../products/WLC/contexts/WeekContext';
import { useTrack } from '../../products/WLC/contexts/TrackContext';
import { SocialPlusComments } from '../../../../components/SocialPlusComments';
import { useWLC } from '../../products/WLC/contexts/WLCContext';

const { weekChecklistAndResources } = styles;

const Week = ({
  user,
  authToken,
  overrideSlug,
  toursCompleted = {},
  toursLoaded,
  get,
  post,
  refreshToken,
  revokeDiscourseAPIKey,
  updateTourStatus
}) => {
  const location = useLocation();
  const history = useHistory();
  const params = useParams();
  const slug = overrideSlug || params?.slug || '';
  const productInPath = getProductFromPath();
  const isGettingStarted = isGettingStartedSlug(slug);
  const isTrackPreview = !!location?.state?.isTrackPreview;
  const breakpoint = useBootstrapBreakpoint();

  const isMobile = breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md';
  console.log('WEEK PROPS:', slug, overrideSlug, params.slug, isGettingStarted);
  console.log('GETTING_STARTED_RESOURCES', GETTING_STARTED_RESOURCES);
  // Fetch week data
  const { contentItem } = useWeekData(slug, productInPath, authToken, user);
  const { setWeek } = useWeek();
  const { isLegacyMember, isOnboard } = useWLC();
  const { metadata: trackMetadata } = useTrack() || {};

  // Sync fetched week data into WeekContext so sub-components
  // (TrackWeekHeader, ActionVideo, RecipesList, etc.) display the correct week
  useEffect(() => {
    setWeek(contentItem);
  }, [contentItem]);

  // Onboarding state (only initialize if this is onboarding)
  const { onboardingState, saveState } = useOnboardingState(isGettingStarted && contentItem?.id, get, post);

  // Scroll to comments
  const { commentsWrapperRef, highlightedCommentRef, scrollToContent } = useScrollToComments();

  // Tour state
  const [runTour, setRunTour] = useState(true);

  // Computed values
  const showTour = useMemo(
    () => !isGettingStarted && !!toursLoaded && !toursCompleted.week,
    [isGettingStarted, toursLoaded, toursCompleted]
  );

  // Navigation helpers
  const moveToCurrentWeek = useCallback(() => {
    window.PREVENT_NAV_TO_GETTING_STARTED = true;
    history.push(`/products/${productInPath}/current-week`);
  }, [history, productInPath]);

  const handleGetStartedClick = useCallback(() => {
    history.push(`/products/${productInPath}/`);
  }, [history, productInPath]);

  // Onboarding handlers
  const handleOnboardComponentUpdate = useCallback(() => {
    saveState({
      checklistComplete: true,
      complete: true
    });
  }, [saveState]);

  const handleOnboardAlertLinkClick = useCallback(
    e => {
      e.preventDefault();

      if (onboardingState.checklistComplete || onboardingState.neverShowAgain) {
        return moveToCurrentWeek();
      }

      const newDismissedTimes = onboardingState.dismissedTimes + 1;
      saveState({ dismissedTimes: newDismissedTimes });

      if (newDismissedTimes > 1) {
        saveState({ showOnboardSkipModal: true });
      }
    },
    [onboardingState, saveState, moveToCurrentWeek]
  );

  const handleOnboardSkipModalButtonClick = useCallback(
    skip => {
      if (skip) {
        saveState({ neverShowAgain: true });
      }
      moveToCurrentWeek();
    },
    [saveState, moveToCurrentWeek]
  );

  const handleUserClickComplete = useCallback(() => {
    if (onboardingState.showCompleteModal) {
      saveState({ showCompleteModal: false });
    }
    moveToCurrentWeek();
  }, [onboardingState.showCompleteModal, saveState, moveToCurrentWeek]);

  const handleUserCloseModal = useCallback(() => {
    saveState({ showCompleteModal: false });
  }, [saveState]);

  // Tour handler
  const handleJoyrideCallback = useCallback(
    data => {
      const { status } = data;
      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        setRunTour(false);
        if (updateTourStatus) {
          updateTourStatus(`week`, true);
        }
      }
    },
    [updateTourStatus, isGettingStarted]
  );

  // Show loading state
  if (!contentItem) {
    return <WeekSkeleton isGettingStarted={isGettingStarted} isMobile={isMobile} isTrackPreview={isTrackPreview} />;
  }

  console.log('WEEK CONTENT ITEM:', contentItem);

  const {
    id,
    slug: contentSlug,
    title,
    video,
    checklist,
    recipesList,
    resourcesList,
    userAnalytics,
    actionHour,
    data: { startTime, endTime, tip } = {}
  } = contentItem;

  const weekNameDate =
    contentSlug === GETTING_STARTED_SLUG ? title : `${title} (${getWeekNameDate(startTime, endTime)})`;

  return (
    <>
      {!isOnboard && !isLegacyMember ? (
        <Container>
          <Alert>
            <p>
              <strong>👋 What are your health goals?</strong>
              <br />
              Tell us a little about yourself so we can tailor your recipes, guides, and emails just for you.
            </p>
            <Button onClick={handleGetStartedClick}>Get Started →</Button>
          </Alert>
        </Container>
      ) : null}

      <Container key={id}>
        {/*<ReactJoyride
          continuous
          callback={handleJoyrideCallback}
          run={runTour && showTour}
          steps={TOUR_STEPS}
          scrollToFirstStep
          showProgress
          showSkipButton
          styles={{
            options: {
              zIndex: 10000
            }
          }}
        />*/}

        <TrackWeekHeader weekTitle={title} weekSlug={contentSlug} />
      </Container>

      {video && (
        <div className="background-light-purple">
          <Container>
            <ActionVideo
              userAnalytics={userAnalytics}
              {...(isGettingStarted ? { title: 'Welcome!', noButton: true } : {})}
            />
          </Container>
        </div>
      )}
      {isGettingStarted && (
        <div className="background-dark-purple">
          <Container className="week-tip">
            <Row className="py-5">
              <Col className="text-white">
                <ReactMarkdown>{tip}</ReactMarkdown>
              </Col>
            </Row>
          </Container>
        </div>
      )}

      {!isGettingStarted && recipesList && (
        <Container>
          <RecipesList
            userAnalytics={createAnalyticsContext(userAnalytics, { link_type: 'resource' })}
            className="weekRecipesList pb-5"
            colSizes={{ xs: 12, sm: 10, md: 4 }}
          />
        </Container>
      )}

      {(isMobile || isGettingStarted) && (
        <div className="background-light-gray">
          <Container>
            <Row className={classNames(weekChecklistAndResources)}>
              <Col xs={12} lg={6} className="weekChecklist" style={{ marginTop: '3.6rem' }}>
                <ChecklistCard
                  checklist={checklist}
                  userAnalytics={userAnalytics}
                  title={isGettingStarted ? 'Your Starting Checklist' : 'This Week’s Action Items'}
                />
              </Col>
              {isGettingStarted && !isMobile ? (
                <Col xs={12} lg={6} className="weekChecklist" style={{ marginTop: '3.6rem' }}>
                  <GettingStartedSuccessCard />
                </Col>
              ) : null}
            </Row>
          </Container>
        </div>
      )}
      <div className="background-dark-purple">
        <Container className="week-tip">
          <Row className="py-5">
            <Col className="text-white">
              <ReactMarkdown>
                {isGettingStarted
                  ? '**Remember: progress, not perfection.**\n\nEvery step matters, and you’ve got an entire community behind you.'
                  : tip}
              </ReactMarkdown>
            </Col>
          </Row>
        </Container>
      </div>
      {isGettingStarted && isMobile ? <GettingStartedSuccessSection /> : null}
      {isGettingStarted && (
        <Container>
          {recipesList && (
            <RecipesList
              userAnalytics={createAnalyticsContext(userAnalytics, { link_type: 'resource' })}
              className="weekRecipesList pb-5"
              colSizes={{ xs: 12, sm: 10, md: 4 }}
            />
          )}
        </Container>
      )}
      {!isGettingStarted && resourcesList?.Children?.length > 0 && <ResourcesSection cards={resourcesList?.Children} />}
      {isGettingStarted && (
        <GettingStartedTutorialSection
          cards={GETTING_STARTED_RESOURCES}
          tutorialVideoUrl={GETTING_STARTED_TUTORIAL_VIDEO_URL}
          isMobile={isMobile}
        />
      )}
      {isGettingStarted && (
        <div className="background-dark-purple week-tip">
          <Container>
            <Row className="py-5">
              <Col className="text-white">
                <ReactMarkdown>{`**You’re Ready!**\n\nNext up is your first weekly guide, tailored to your health track. It’ll include an Action of the Week, recipes, resources, an action items checklist, your monthly Action Hour, and upcoming live events.`}</ReactMarkdown>
              </Col>
            </Row>
          </Container>
        </div>
      )}
      {!isGettingStarted && isMobile && <TrackWeekInterstitial weekSlug={contentSlug} />}
      {!isMobile && !isGettingStarted && (
        <div className="background-light-gray">
          <Container>
            <Row className={classNames('py-5', weekChecklistAndResources)}>
              <Col xs={12} lg={6}>
                <ChecklistCard checklist={checklist} userAnalytics={userAnalytics} />
              </Col>
              <Col xs={12} lg={6}>
                {!isGettingStarted ? (
                  <ActionHourCard
                    slug="wlc-action-hour-christopher-gardner-may-2025"
                    authToken={authToken}
                    product="wlc"
                    weekNumber={1}
                  />
                ) : (
                  <GettingStartedSuccessCard />
                )}
              </Col>
            </Row>
          </Container>
        </div>
      )}
      {!isGettingStarted ? (
        <>
          {!isTrackPreview && isMobile && (
            <ActionHourSection
              slug="wlc-action-hour-christopher-gardner-may-2025"
              authToken={authToken}
              product="wlc"
              weekNumber={1}
            />
          )}
          {!isMobile && <TrackWeekInterstitial weekSlug={contentSlug} />}
          {!isTrackPreview && (
            <NextLiveEventSection
              authToken={authToken}
              productSlug={productInPath}
              rootContentSlug="wlc"
              eventSlug="wlc-next-live-event"
              liveEventsHubLink="https://frn.co/wlc-zoom-hub"
            />
          )}
          {false && !isTrackPreview && (
            <div ref={commentsWrapperRef}>
              <ForumWrapper
                service="week"
                product={productInPath}
                slug={contentSlug}
                scrollToMe={scrollToContent}
                passedReactRef={highlightedCommentRef}
                pageTitle={title}
                user={user}
                refreshToken={refreshToken}
                revokeDiscourseAPIKey={revokeDiscourseAPIKey}
                forumType="ContentItem"
              />
            </div>
          )}
        </>
      ) : (
        <GettingStartedTrackList />
      )}
      {!isGettingStarted && trackMetadata?.slug && !isTrackPreview && (
        <Container>
          <Row>
            <Col>
              <SocialPlusComments referenceId={`${productInPath}-${trackMetadata?.slug}`} referenceType="content" />
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};

Week.propTypes = {
  user: PropTypes.shape({
    UUID: PropTypes.string
  }),
  authToken: PropTypes.string,
  overrideSlug: PropTypes.string,
  toursCompleted: PropTypes.shape({
    week: PropTypes.bool
  }),
  toursLoaded: PropTypes.bool,
  get: PropTypes.func.isRequired,
  post: PropTypes.func.isRequired,
  refreshToken: PropTypes.func,
  revokeDiscourseAPIKey: PropTypes.func,
  updateTourStatus: PropTypes.func
};

const mapStateToProps = state => ({
  user: state.user,
  authToken: state.user.authToken
});

const mapDispatchToProps = dispatch => ({
  get: contentId => dispatch(userActions.getUserData(contentId)),
  post: (contentId, data) => dispatch(userActions.postUserData(contentId, data)),
  refreshToken: userActions.refreshToken,
  revokeDiscourseAPIKey: userActions.revokeDiscourseAPIKey
});

export default connect(mapStateToProps, mapDispatchToProps)(Week);
